/**
 * A workspace is what a signed-in person operates: a tenant (club), later a
 * venue or an operator (kiosk). Rows come from the get_my_workspaces() RPC;
 * this module is the pure part (keys, selection, legacy fallback) so it can
 * be unit-tested without React or Supabase.
 */
export type WorkspaceKind = 'tenant' | 'venue' | 'operator';

export interface Workspace {
  kind: WorkspaceKind;
  id: string;
  name: string;
  organization_id: string | null;
  role: string;
  stadium_id: string | null;
  stadium_name: string | null;
  has_own_operator: boolean;
  served_by_operator_id: string | null;
  served_by_operator_name: string | null;
}

export const ACTIVE_WORKSPACE_KEY = 'active_workspace';
/** The pre-workspace key; read once to migrate, then superseded. */
export const LEGACY_ACTIVE_CLUB_KEY = 'active_club_id';
/**
 * IDSeat "Open portal": the one workspace an IDSeat admin is viewing without
 * being a member of it. Kept apart from ACTIVE_WORKSPACE_KEY so closing it
 * never loses the person's own choice.
 */
export const VIEW_AS_KEY = 'idseat_view_as';

const KINDS: WorkspaceKind[] = ['tenant', 'venue', 'operator'];

export function readViewAs(): { kind: WorkspaceKind; id: string } | null {
  try { return parseWorkspaceKey(localStorage.getItem(VIEW_AS_KEY)); } catch { return null; }
}

export function writeViewAs(kind: WorkspaceKind, id: string): void {
  try { localStorage.setItem(VIEW_AS_KEY, workspaceKey(kind, id)); } catch { /* private mode: in-memory only */ }
}

export function clearViewAs(): void {
  try { localStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
}

/** The person's own workspaces plus the viewed one, unless they already hold it. */
export function mergeViewAs(workspaces: Workspace[], viewed: Workspace | null): Workspace[] {
  if (!viewed) return workspaces;
  if (workspaces.some((w) => w.kind === viewed.kind && w.id === viewed.id)) return workspaces;
  return [...workspaces, viewed];
}

export function workspaceKey(kind: WorkspaceKind, id: string): string {
  return `${kind}:${id}`;
}

export function parseWorkspaceKey(raw: string | null): { kind: WorkspaceKind; id: string } | null {
  if (!raw) return null;
  const idx = raw.indexOf(':');
  if (idx <= 0) return null;
  const kind = raw.slice(0, idx) as WorkspaceKind;
  const id = raw.slice(idx + 1);
  if (!KINDS.includes(kind) || !id) return null;
  return { kind, id };
}

/**
 * Stored key wins when it still exists; otherwise the legacy club id (so a
 * person who chose a club before this change lands in the same club); else
 * the first workspace; null when there is none.
 */
export function pickActiveWorkspace(
  workspaces: Workspace[],
  storedKey: string | null,
  legacyClubId: string | null,
): Workspace | null {
  const stored = parseWorkspaceKey(storedKey);
  if (stored) {
    const hit = workspaces.find((w) => w.kind === stored.kind && w.id === stored.id);
    if (hit) return hit;
  }
  if (legacyClubId) {
    const hit = workspaces.find((w) => w.kind === 'tenant' && w.id === legacyClubId);
    if (hit) return hit;
  }
  return workspaces[0] ?? null;
}

/** Fallback when the RPC is unavailable: tenant workspaces from profile rows, de-duplicated. */
export function workspacesFromProfiles(
  profiles: { club_id: string | null; clubs: { name: string } | null }[],
): Workspace[] {
  const seen = new Set<string>();
  const out: Workspace[] = [];
  for (const p of profiles) {
    if (!p.club_id || seen.has(p.club_id)) continue;
    seen.add(p.club_id);
    out.push({
      kind: 'tenant',
      id: p.club_id,
      name: p.clubs?.name ?? 'Unknown club',
      organization_id: null,
      role: 'admin',
      stadium_id: null,
      stadium_name: null,
      has_own_operator: true,
      served_by_operator_id: null,
      served_by_operator_name: null,
    });
  }
  return out;
}

/** The portal a workspace kind opens, as shown in the shell and on the landing page. */
export function workspacePortalLabel(kind: WorkspaceKind): string {
  switch (kind) {
    case 'operator':
      return 'Kiosk Portal';
    case 'venue':
      return 'Stadium Portal';
    default:
      return 'Tenant Portal';
  }
}

/** Where a workspace kind lives in the app. */
export function workspaceHome(kind: WorkspaceKind): string {
  switch (kind) {
    case 'operator':
      return '/kiosk';
    case 'venue':
      return '/venue';
    default:
      return '/club';
  }
}

/** Human label for a workspace kind, as shown in the switcher and the login picker. */
export function workspaceKindLabel(kind: WorkspaceKind): string {
  switch (kind) {
    case 'tenant':
      return 'Tenant Portal';
    case 'venue':
      return 'Stadium Portal';
    case 'operator':
      return 'Kiosk Portal';
  }
}
